import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowRight,
  LuBox,
  LuCheck,
  LuClipboardList,
  LuClock3,
  LuCloudUpload,
  LuCreditCard,
  LuEllipsis,
  LuHeadphones,
  LuInfo,
  LuLightbulb,
  LuMail,
  LuMessageCircle,
  LuPackage,
  LuScale,
  LuShieldCheck,
  LuUserCog,
  LuWrench,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import PhoneCountryCodeSelect from "../../components/PhoneCountryCodeSelect";
import StorageService from "../../services/StorageService";
import {
  createMySupportTicket,
  ticketExtractMessage,
} from "../../services/supportTicket.service";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/raise_support_ticket.scss";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"];
const MAX_DESCRIPTION = 3000;

const TICKET_CATEGORIES = [
  { id: "account_profile", label: "Account & Profile", icon: LuUserCog, tone: "orange" },
  { id: "technical", label: "Technical Issue", icon: LuWrench, tone: "blue" },
  { id: "payment_billing", label: "Payment & Billing", icon: LuCreditCard, tone: "green" },
  { id: "product_listing", label: "Product & Listing", icon: LuBox, tone: "purple" },
  { id: "orders_transactions", label: "Orders & Transactions", icon: LuClipboardList, tone: "cyan" },
  { id: "feature_request", label: "Feature Request", icon: LuLightbulb, tone: "amber" },
  { id: "other", label: "Other", icon: LuEllipsis, tone: "gray" },
];

const MODULE_OPTIONS = [
  "Dashboard",
  "RFQ & Quotations",
  "Orders & Purchase",
  "Payments & Invoices",
  "Logistics & Shipping",
  "Products & Listings",
  "Account & Profile",
  "Notifications",
  "Other",
];

const PROCESS_STEPS = [
  {
    title: "Raise Ticket",
    description: "Submit your issue with relevant details and attachments.",
    icon: LuClipboardList,
    tone: "blue",
  },
  {
    title: "Review Process",
    description: "Our support team reviews and may ask for more information.",
    icon: LuCheck,
    tone: "green",
  },
  {
    title: "Resolution",
    description: "We resolve the issue and notify you by email.",
    icon: LuScale,
    tone: "orange",
  },
];

const TRUST_ITEMS = [
  {
    title: "24 Hours",
    subtitle: "Average Response Time",
    icon: LuClock3,
    tone: "orange",
  },
  {
    title: "Expert Support",
    subtitle: "Trained Professionals",
    icon: LuHeadphones,
    tone: "green",
  },
  {
    title: "Secure & Confidential",
    subtitle: "Your data is safe with us",
    icon: LuShieldCheck,
    tone: "purple",
  },
  {
    title: "SLA Commitment",
    subtitle: "We are here to help",
    icon: LuPackage,
    tone: "coral",
  },
];

function getUserDisplayName() {
  const user = StorageService.getUser() || {};
  return (
    user.name ||
    user.fullName ||
    user.full_name ||
    [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(" ") ||
    ""
  );
}

function initialForm() {
  const user = StorageService.getUser() || {};
  return {
    category: "account_profile",
    subject: "",
    priority: "",
    module: "",
    referenceNumber: "",
    description: "",
    fullName: getUserDisplayName(),
    email: user.email || user.email_address || "",
    countryCode: "+91",
    phone: String(user.phone || user.mobile || user.mobile_number || "").replace(/^\+?\d{1,3}\s*/, ""),
    consent: false,
    attachment: null,
  };
}

function priorityBadgeClass(priority) {
  const value = String(priority || "").toLowerCase();
  if (value === "urgent" || value === "high") return "raise-ticket-priority-badge is-high";
  if (value === "low") return "raise-ticket-priority-badge is-low";
  if (value === "medium") return "raise-ticket-priority-badge is-medium";
  return "raise-ticket-priority-badge is-empty";
}

export default function RaiseSupportTicketPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const selectFile = (file) => {
    if (!file) {
      setForm((prev) => ({ ...prev, attachment: null }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error("Attachment must be 10MB or smaller.");
      return;
    }
    const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error("Unsupported file type. Allowed: JPG, PNG, PDF, DOC, XLS.");
      return;
    }
    setForm((prev) => ({ ...prev, attachment: file }));
  };

  const validate = () => {
    if (!form.category) {
      toast.error("Please select a ticket category.");
      return false;
    }
    if (!form.subject.trim()) {
      toast.error("Please enter a subject.");
      return false;
    }
    if (!form.priority) {
      toast.error("Please select a priority.");
      return false;
    }
    if (!form.description.trim() || form.description.trim().length < 10) {
      toast.error("Please describe your issue (at least 10 characters).");
      return false;
    }
    if (!form.fullName.trim()) {
      toast.error("Please enter your full name.");
      return false;
    }
    if (!form.email.trim()) {
      toast.error("Please enter your email.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15) {
      toast.error("Please enter a valid phone number.");
      return false;
    }
    if (!form.consent) {
      toast.error("Please confirm that the information provided is accurate.");
      return false;
    }
    return true;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    if (!StorageService.getToken()) {
      toast.error("Please log in to raise a support ticket.");
      navigate("/login", { state: { from: "/help-center/raise-ticket" } });
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("categoryCode", form.category);
      payload.append("subject", form.subject.trim());
      payload.append("priority", form.priority);
      payload.append("affectedModule", form.module || "");
      payload.append("referenceNumber", form.referenceNumber.trim());
      payload.append("description", form.description.trim());
      payload.append("customerName", form.fullName.trim());
      payload.append("customerEmail", form.email.trim());
      payload.append("customerCountryCode", form.countryCode || "+91");
      payload.append("customerPhone", form.phone.trim());
      payload.append("consentAccepted", "true");
      if (form.attachment) {
        payload.append("attachment", form.attachment);
      }

      const data = await createMySupportTicket(payload);
      const success = data?.successPage || data?.ticket;
      if (success) {
        navigate("/help-center/ticket-created", {
          replace: true,
          state: {
            ticketNumber: data.successPage?.ticketNumber || data.ticket?.ticketNumber,
            category: data.successPage?.category || data.ticket?.categoryLabel,
            priority: data.successPage?.priority || data.ticket?.priority,
            status: data.successPage?.status || data.ticket?.statusLabel || "New",
            expectedResponseTime:
              data.successPage?.expectedResponseTime || data.ticket?.expectedResponseLabel,
            createdAt: data.successPage?.createdAt || data.ticket?.createdAt,
            ticketId: data.ticket?.id,
          },
        });
        return;
      }
      toast.error("Could not raise ticket. Please try again.");
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Could not raise ticket. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="raise-ticket-page">
      <B2BHubHomeHeader />

      <main className="raise-ticket-main">
        <nav className="raise-ticket-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">Raise Ticket</span>
        </nav>

        <header className="raise-ticket-heading">
          <div>
            <h1>Raise Support Ticket</h1>
            <p>Tell us what you need help with and our team will get back to you.</p>
          </div>
          <Link
            to="/dashboard/help-center/my-tickets"
            className="raise-ticket-btn raise-ticket-btn--ghost raise-ticket-heading__action"
          >
            <LuClipboardList aria-hidden /> My Tickets
          </Link>
        </header>

        <div className="raise-ticket-layout">
          <form className="raise-ticket-form" onSubmit={onSubmit} noValidate>
            <section className="raise-ticket-section">
              <h2>
                1. Ticket Category <em>*</em>
              </h2>
              <div className="raise-ticket-categories" role="radiogroup" aria-label="Ticket category">
                {TICKET_CATEGORIES.map((item) => {
                  const Icon = item.icon;
                  const selected = form.category === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`raise-ticket-category${selected ? " is-selected" : ""} raise-ticket-tone--${item.tone}`}
                      onClick={() => setForm((prev) => ({ ...prev, category: item.id }))}
                    >
                      <span className="raise-ticket-category__icon" aria-hidden>
                        <Icon />
                      </span>
                      <span className="raise-ticket-category__label">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="raise-ticket-section">
              <h2>2. Ticket Details</h2>
              <div className="raise-ticket-grid raise-ticket-grid--details">
                <div className="raise-ticket-grid__row raise-ticket-grid__row--subject-priority">
                  <label className="raise-ticket-field">
                    <span>
                      Subject <em>*</em>
                    </span>
                    <input
                      className="input-pill"
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      maxLength={160}
                      placeholder="Enter a short summary of your issue"
                      required
                    />
                  </label>

                  <label className="raise-ticket-field raise-ticket-field--priority">
                    <span>
                      Priority <em>*</em>
                    </span>
                    <div className="raise-ticket-priority-row">
                      <select className="input-pill" name="priority" value={form.priority} onChange={onChange} required>
                        <option value="">Select priority</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                      </select>
                      {form.priority ? (
                        <span className={priorityBadgeClass(form.priority)}>{form.priority}</span>
                      ) : null}
                    </div>
                  </label>
                </div>

                <div className="raise-ticket-grid__row raise-ticket-grid__row--module-ref">
                  <label className="raise-ticket-field">
                    <span>Affected Module / Feature (Optional)</span>
                    <select className="input-pill" name="module" value={form.module} onChange={onChange}>
                      <option value="">Select module</option>
                      {MODULE_OPTIONS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="raise-ticket-field">
                    <span>Order / Reference Number (Optional)</span>
                    <input
                      className="input-pill"
                      name="referenceNumber"
                      value={form.referenceNumber}
                      onChange={onChange}
                      maxLength={80}
                      placeholder="e.g. PO-2026-00123"
                    />
                    <small>Provide if issue is related to an order, RFQ, or transaction.</small>
                  </label>
                </div>
              </div>
            </section>

            <section className="raise-ticket-section">
              <h2>
                3. Describe Your Issue <em>*</em>
              </h2>
              <label className="raise-ticket-field">
                <span className="visually-hidden">Description</span>
                <span className="raise-ticket-textarea">
                  <textarea
                    className="input-area"
                    name="description"
                    value={form.description}
                    onChange={onChange}
                    maxLength={MAX_DESCRIPTION}
                    rows={7}
                    placeholder="Describe the issue in detail. Include steps to reproduce, expected vs actual behaviour, and any error messages."
                    required
                  />
                  <span>
                    {form.description.length} / {MAX_DESCRIPTION}
                  </span>
                </span>
              </label>
            </section>

            <section className="raise-ticket-section">
              <h2>4. Attachments (Optional)</h2>
              <div
                className={`raise-ticket-upload${dragging ? " is-dragging" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  selectFile(event.dataTransfer.files?.[0]);
                }}
              >
                <LuCloudUpload aria-hidden />
                <div>
                  <strong>
                    {form.attachment ? form.attachment.name : "Drag and drop files here or "}
                    {!form.attachment ? (
                      <button type="button" onClick={() => fileInputRef.current?.click()}>
                        Browse Files
                      </button>
                    ) : null}
                  </strong>
                  {form.attachment ? (
                    <button
                      type="button"
                      className="raise-ticket-upload__change"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change file
                    </button>
                  ) : (
                    <span>Supported: JPG, PNG, PDF, DOC, XLS (Max 10 MB per file)</span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(event) => selectFile(event.target.files?.[0])}
                  hidden
                />
              </div>
            </section>

            <section className="raise-ticket-section">
              <h2>5. Your Contact Information</h2>
              <div className="raise-ticket-grid raise-ticket-grid--contact">
                <label className="raise-ticket-field">
                  <span>
                    Full Name <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="fullName"
                    value={form.fullName}
                    onChange={onChange}
                    placeholder="Enter your full name"
                    required
                  />
                </label>

                <label className="raise-ticket-field">
                  <span>
                    Email <em>*</em>
                  </span>
                  <span className="raise-ticket-input-icon">
                    <LuMail aria-hidden />
                    <input
                      className="input-pill"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@company.com"
                      required
                    />
                  </span>
                </label>

                <label className="raise-ticket-field raise-ticket-field--phone">
                  <span>
                    Phone Number <em>*</em>
                  </span>
                  <span className="raise-ticket-phone">
                    <PhoneCountryCodeSelect
                      name="countryCode"
                      value={form.countryCode}
                      onChange={onChange}
                      aria-label="Country code"
                    />
                    <input
                      className="input-pill"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="Enter phone number"
                      required
                    />
                  </span>
                </label>
              </div>
            </section>

            <label className="raise-ticket-consent">
              <input
                type="checkbox"
                name="consent"
                checked={form.consent}
                onChange={onChange}
              />
              <span>
                I confirm that the information provided is accurate and understand that our team
                will review my ticket.
              </span>
            </label>

            <div className="raise-ticket-actions">
              <button
                type="button"
                className="raise-ticket-btn raise-ticket-btn--ghost"
                onClick={() => navigate("/help-center")}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="raise-ticket-btn raise-ticket-btn--primary"
                disabled={submitting}
              >
                {submitting ? "Raising…" : "Raise Ticket"}
                <LuArrowRight aria-hidden />
              </button>
            </div>
          </form>

          <aside className="raise-ticket-sidebar">
            <article className="raise-ticket-side-card">
              <h3>How it works</h3>
              <ol className="raise-ticket-steps">
                {PROCESS_STEPS.map((step) => {
                  const Icon = step.icon;
                  return (
                    <li key={step.title}>
                      <span className={`raise-ticket-steps__icon raise-ticket-tone--${step.tone}`} aria-hidden>
                        <Icon />
                      </span>
                      <div>
                        <strong>{step.title}</strong>
                        <p>{step.description}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </article>

            <article className="raise-ticket-side-card raise-ticket-side-card--note">
              <div className="raise-ticket-note">
                <span className="raise-ticket-note__icon" aria-hidden>
                  <LuInfo />
                </span>
                <div>
                  <strong>Important Note</strong>
                  <p>
                    Provide as much detail as possible — screenshots, order IDs, and error messages
                    help us resolve your ticket faster.
                  </p>
                </div>
              </div>
            </article>

            <article className="raise-ticket-side-card">
              <h3>Need Immediate Help?</h3>
              <ul className="raise-ticket-help-list">
                <li>
                  <LuMail aria-hidden />
                  <a href="mailto:support@trocanet.com">support@trocanet.com</a>
                </li>
              </ul>
              <Link to="/contact?enquiry=general" className="raise-ticket-live-chat">
                <LuMessageCircle aria-hidden />
                Live Chat
              </Link>
            </article>
          </aside>
        </div>

        <section className="raise-ticket-trust" aria-label="Support commitments">
          {TRUST_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="raise-ticket-trust__card">
                <span className={`raise-ticket-trust__icon raise-ticket-tone--${item.tone}`} aria-hidden>
                  <Icon />
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.subtitle}</span>
                </div>
              </article>
            );
          })}
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
